import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort boundary. A render crash shows a recoverable screen instead of a
 * blank page; the technical detail goes to the console, never to the user.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg px-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 text-center">
          <span className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-danger/10 text-danger">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </span>
          <h1 className="text-sm font-semibold text-fg">This page stopped responding</h1>
          <p className="mt-2 text-sm text-subtle">
            Something in the interface failed to render. Reloading usually clears it.
          </p>
          <Button
            variant="primary"
            className="mt-5"
            leadingIcon={<RefreshCw className="h-3.5 w-3.5" />}
            onClick={() => window.location.reload()}
          >
            Reload the page
          </Button>
        </div>
      </div>
    );
  }
}
